// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByNodeId, LinkModel, mapLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getDataLayer } from "@turnerBlueprint/XDL/Utilities/DataLayer";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    alignment = "alignment",
    links = "links",
    backgroundColor = "backgroundColor",
    textColor = "textColor",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.alignment]: "left" | "center" | "right";
        [fields.links]: LinkModel[];
        [fields.backgroundColor]: string;
        [fields.textColor]: string;
    };
    extendedStyles?: FeaturedSectionStyles;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const links = mapLinks<LinkModel, { openInNewWindow: boolean; overriddenTitle: string }>(
        state,
        ownProps.fields.links,
        widgetLink => ({
            openInNewWindow: widgetLink.fields.openInNewWindow,
            overriddenTitle: widgetLink.fields.overriddenTitle,
        }),
    );
    return {
        links,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasHistory;

export interface FeaturedSectionStyles {
    featuredSectionWrapper?: InjectableCss;
    linkWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    buttonBreakpointStyles: InjectableCss;
    titleWrapper: InjectableCss;
    linksContainer: InjectableCss;
}

export const featuredSectionStyles: FeaturedSectionStyles = {
    featuredSectionWrapper: {
        css: css`
            text-transform: uppercase;
            padding: 10px 0;
        `,
    },
    linkWrapper: {
        css: css`
            padding: 1px 18px 0 0;
        `,
    },
    buttonBreakpointStyles: {
        css: css`
            display: inline=flex !important;
            flex-wrap: wrap;
            padding-bottom: 15px;
            padding-right: 20px;
            max-width: 100%;
        `,
    },
    titleWrapper: {
        css: css`
            margin-bottom: 15px;
        `,
    },
    linksContainer: {
        css: css`
            display: flex;
            flex-wrap: wrap;
        `,
    },
};

const FeaturedSection: React.FC<Props> = ({ fields, links, extendedStyles, history }) => {
    const [styles] = React.useState(() => mergeToNew(featuredSectionStyles, extendedStyles));

    const alignmentStyles = css`
        text-align: ${fields.alignment || "right"};
        ${styles.featuredSectionWrapper?.css}
    `;

    const routeToClick = (link: string | undefined, clickText: string | undefined) => {
        if (!link) {
            return;
        }

        const dataLayer = getDataLayer();
        if (dataLayer) {
            const googleAnalyticsData = {
                event: "featured_topics_buttons",
                text: clickText,
                page: document.location.href,
                destination: link,
            };
            dataLayer.push(googleAnalyticsData);
        }
        history.push(link);
    };

    return (
        <StyledWrapper css={alignmentStyles}>
            <StyledWrapper {...styles.titleWrapper}>
                <Typography as="h3" {...styles.title} size={17} weight={800}>
                    {fields.title}
                </Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.linksContainer}>
                {links.map((link, index) => (
                    <StyledWrapper
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                        data-test-selector="custom_content_service_button"
                        {...styles.buttonBreakpointStyles}
                        className="custom_content_service_button homepageProductbuttons"
                    >
                        <Button
                            size={30}
                            shape="pill"
                            typographyProps={{ color: fields.textColor, size: 14, weight: 600 }}
                            css={css`
                                background-color: ${fields.backgroundColor};
                                background: ${fields.backgroundColor};
                                border: 0.1em solid ${fields.textColor};
                                word-break: normal;
                                padding-bottom: 5px !important;
                                padding-top: 5px !important;
                            `}
                            hoverStyle={{ background: fields.backgroundColor, borderColor: fields.textColor }}
                            activeStyle={{ background: fields.backgroundColor, borderColor: fields.textColor }}
                            onClick={() => routeToClick(link.url, link.overriddenTitle || link.title)}
                            className="homepageProductbuttons"
                        >
                            {link.overriddenTitle || link.title}
                        </Button>
                    </StyledWrapper>
                ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withHistory(FeaturedSection)),
    definition: {
        group: "Basic",
        icon: "Logo",
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.alignment,
                editorTemplate: "DropDownField",
                defaultValue: "left",
                fieldType: "General",
                options: [
                    {
                        displayName: "Left",
                        value: "left",
                    },
                    {
                        displayName: "Center",
                        value: "center",
                    },
                    {
                        displayName: "Right",
                        value: "right",
                    },
                ],
                hideEmptyOption: true,
            },
            {
                fieldType: "General",
                name: fields.textColor,
                editorTemplate: "ColorPickerField",
                displayName: "Text Color",
                defaultValue: "black",
            },
            {
                fieldType: "General",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Background Color",
                defaultValue: "white",
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        return value; // TODO ISC-10781 make this work
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
